import { useEffect, useMemo, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { utils, writeFileXLSX } from "xlsx";
import DataService from "../services/data.service";
import {
  buildReportSummary,
  formatCurrency,
  formatDateLabel,
  getStatusMeta,
  toInputDateValue,
} from "../common/reporting";

const DisbursementsReportAdmin = () => {
  const today = new Date();
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const [fromDate, setFromDate] = useState(toInputDateValue(monthStart));
  const [toDate, setToDate] = useState(toInputDateValue(today));
  const [tableRows, setTableRows] = useState([]);
  const [retryIds, setRetryIds] = useState([]);
  const [showDetail, setShowDetail] = useState(false);
  const [details, setDetails] = useState({});
  const [summary, setSummary] = useState(buildReportSummary([]));
  const [searchPhoneNumber, setSearchPhoneNumber] = useState("");
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");

  useEffect(() => {
    DataService.getAllClients().then((response) => {
      setClients(response.data);

      if (response.data.length) {
        setClientId(response.data[0].accountNumber);
      }
    });
  }, []);

  const exportToCSV = () => {
    const worksheet = utils.json_to_sheet(tableRows);
    const workbook = utils.book_new();
    utils.book_append_sheet(workbook, worksheet, "Admin Disbursements");
    writeFileXLSX(workbook, "admin-disbursements-report.xlsx");
  };

  const exportPDF = () => {
    const doc = new jsPDF("portrait", "pt", "A4");
    doc.setFontSize(15);
    doc.text("Admin Disbursements Report", 40, 40);
    doc.autoTable({
      startY: 55,
      head: [["Phone Number", "Amount", "Customer Name", "Status", "Mpesa Ref", "Date"]],
      body: tableRows.map((row) => [
        row.phoneNumber,
        formatCurrency(row.amount),
        row.customerName,
        getStatusMeta(row.status, row.statusMessage).label,
        row.mpesaRef,
        formatDateLabel(row.transactionDate),
      ]),
    });
    doc.save("admin-disbursements-report.pdf");
  };

  const loadTransactions = () => {
    const searchPhone = searchPhoneNumber !== "" ? searchPhoneNumber : "all";

    DataService.fetchDisbursementReport(fromDate, toDate, clientId, searchPhone, 0).then(
      (response) => {
        setTableRows(response.data);
        setRetryIds([]);
        setSummary(buildReportSummary(response.data));
      }
    );
  };

  const handleRetryTransactions = () => {
    DataService.retryTransactions(retryIds).then(() => {
      loadTransactions();
    });
  };

  const selectedClient = useMemo(() => {
    return clients.find((client) => client.accountNumber === clientId);
  }, [clientId, clients]);

  return (
    <div className="portal-page-stack">
      <section className="content-card content-card--padded">
        <div className="section-heading">
          <div>
            <p className="section-heading__eyebrow">Portfolio filters</p>
            <h4>Search across clients</h4>
          </div>
          <div className="button-row">
            <Button variant="outline-primary" onClick={exportToCSV}>
              Export Excel
            </Button>
            <Button variant="outline-primary" onClick={exportPDF}>
              Export PDF
            </Button>
          </div>
        </div>
        <div className="portal-form-grid portal-form-grid--report">
          <Form.Group controlId="adminReportFromDate">
            <Form.Label>From date</Form.Label>
            <Form.Control type="date" value={fromDate} onChange={(event) => setFromDate(event.target.value)} />
          </Form.Group>
          <Form.Group controlId="adminReportToDate">
            <Form.Label>To date</Form.Label>
            <Form.Control type="date" value={toDate} onChange={(event) => setToDate(event.target.value)} />
          </Form.Group>
          <Form.Group controlId="adminReportPhoneNumber">
            <Form.Label>Phone number</Form.Label>
            <Form.Control
              type="text"
              value={searchPhoneNumber}
              onChange={(event) => setSearchPhoneNumber(event.target.value)}
              placeholder="Search recipient"
            />
          </Form.Group>
          <Form.Group controlId="adminReportClient">
            <Form.Label>Client</Form.Label>
            <Form.Control as="select" value={clientId} onChange={(event) => setClientId(event.target.value)}>
              {clients.map((client) => (
                <option value={client.accountNumber} key={client.accountNumber}>
                  {client.clientName}
                </option>
              ))}
            </Form.Control>
          </Form.Group>
        </div>
        <div className="button-row mt-4">
          <Button variant="primary" onClick={loadTransactions} disabled={!clientId}>
            Fetch transactions
          </Button>
          <Button variant="outline-secondary" onClick={handleRetryTransactions} disabled={!retryIds.length}>
            Retry selected ({retryIds.length})
          </Button>
        </div>
      </section>

      <section className="metrics-grid">
        <article className="metric-card metric-card--highlight">
          <p className="metric-card__label">Selected client</p>
          <h4>{selectedClient ? selectedClient.clientName : "Choose a client"}</h4>
          <span>{selectedClient ? selectedClient.accountNumber : "Client account"}</span>
        </article>
        <article className="metric-card">
          <p className="metric-card__label">Successful</p>
          <h4>{summary.successful}</h4>
          <span>Successful transactions in the selected client report.</span>
        </article>
        <article className="metric-card">
          <p className="metric-card__label">Exceptions</p>
          <h4>{summary.failed}</h4>
          <span>Transactions that need review or retry handling.</span>
        </article>
        <article className="metric-card">
          <p className="metric-card__label">Payments value</p>
          <h4>{formatCurrency(summary.payments)}</h4>
          <span>Total successful payment value in the current result set.</span>
        </article>
      </section>

      <section className="content-card content-card--padded">
        <div className="section-heading">
          <div>
            <p className="section-heading__eyebrow">Results</p>
            <h4>Cross-client disbursement activity</h4>
          </div>
          <span className="portal-pill portal-pill--outline">{tableRows.length} transactions</span>
        </div>
        {tableRows.length ? (
          <div className="table-responsive portal-table-wrap">
            <table className="table portal-table align-middle mb-0">
              <thead>
                <tr>
                  <th>Phone number</th>
                  <th>Amount</th>
                  <th>Customer</th>
                  <th>Status</th>
                  <th>Mpesa ref</th>
                  <th>Type</th>
                  <th>Date</th>
                  <th>Project</th>
                  <th>Details</th>
                  <th>Retry</th>
                </tr>
              </thead>
              <tbody>
                {tableRows.map((row) => {
                  const statusMeta = getStatusMeta(row.status, row.statusMessage);
                  const retryAllowed =
                    row.transactionType === "Payment" && Number(row.status) !== 2;

                  return (
                    <tr key={row.uniqueId}>
                      <td>{row.phoneNumber}</td>
                      <td>{formatCurrency(row.amount)}</td>
                      <td>{row.customerName}</td>
                      <td>
                        <span className={`status-badge status-badge--${statusMeta.tone}`}>
                          {statusMeta.label}
                        </span>
                      </td>
                      <td>{row.mpesaRef || "—"}</td>
                      <td>{row.transactionType}</td>
                      <td>{formatDateLabel(row.transactionDate)}</td>
                      <td>{row.projectName}</td>
                      <td>
                        <Button
                          variant="link"
                          className="table-link-button"
                          onClick={() => {
                            setShowDetail(true);
                            setDetails(row);
                          }}
                        >
                          View
                        </Button>
                      </td>
                      <td>
                        {retryAllowed ? (
                          <Form.Check
                            type="checkbox"
                            checked={retryIds.includes(row.uniqueId)}
                            onChange={(event) => {
                              if (event.target.checked) {
                                setRetryIds([...retryIds, row.uniqueId]);
                              } else {
                                setRetryIds(retryIds.filter((retryId) => retryId !== row.uniqueId));
                              }
                            }}
                          />
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state">
            <span className="bi bi-clipboard-data" aria-hidden="true" />
            <h5>No report loaded</h5>
            <p>Select a client and fetch transactions to inspect performance across the portfolio.</p>
          </div>
        )}
      </section>

      <Modal show={showDetail} onHide={() => setShowDetail(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Transaction details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="detail-list">
            {[
              ["Customer name", details.customerName],
              ["Amount", formatCurrency(details.amount)],
              ["Charges", formatCurrency(details.chargeAmount)],
              ["Batch reference", details.batchRef],
              ["Conversation ID", details.conversationId],
              ["Description", details.description],
              ["Mpesa reference", details.mpesaRef],
              ["Originator conversation ID", details.originatorConversationId],
              ["Phone number", details.phoneNumber],
              ["Response description", details.responseDescription],
              ["Result code", details.resultCode],
              ["Result description", details.resultDesc],
              ["Status message", details.statusMessage],
              ["Transaction date", formatDateLabel(details.transactionDate)],
              ["Transaction type", details.transactionType],
              ["Unique ID", details.uniqueId],
              ["Retries", details.retries],
            ].map(([label, value]) => (
              <div className="detail-list__row" key={label}>
                <span>{label}</span>
                <strong>{value || "—"}</strong>
              </div>
            ))}
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default DisbursementsReportAdmin;
